import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DateTime } from 'luxon';
import { APP_CONFIG } from '@application/config';
import {
  DreReportPeriodViewModel,
  DreReportViewModel,
} from '../entities/dre-report.model';
import { getDateTime } from '@shared/utils';

/**
 * Converte ISO datetime (formato canônico em todo o app — `2026-05-01T00:00:00.000-03:00`)
 * para o DateOnly (`yyyy-MM-dd`) que o backend `GetDreReport` espera nos
 * query params. Convenção: a conversão acontece **somente** aqui, no
 * boundary HTTP; no resto da aplicação as datas trafegam em ISO completo.
 *
 * Tolerante a entrada já em DateOnly (pass-through quando parsing falha
 * ou quando `toISODate()` é idempotente). Em entrada inválida o valor
 * original é repassado para o backend, que respondem com 400 — fail-loud.
 */
const toBackendDateOnly = (iso: string): string => {
  // `setZone: true` preserva o offset original do ISO — sem isso, Luxon
  // converteria para a zona local antes de extrair `yyyy-MM-dd`, alterando
  // o dia quando o offset cruza o meio-dia UTC. A intenção é preservar
  // o dia que o usuário escolheu, não recalcular em outra zona.
  const parsed = getDateTime(iso);
  if (!parsed?.isValid) return iso;
  return parsed.toISODate() ?? iso;
};

/**
 * Cliente HTTP para o `DreReportController` (treasury_service).
 * Contratos PascalCase 1:1 com o backend; nenhum mapper de casing.
 *
 * Source of truth: `libs/domains/treasury/DRE_REPORT_API.md` e
 * `specs/010-dre-gerencial/contracts/dre-report.api.md`.
 */
@Injectable({ providedIn: 'root' })
export class DreReportService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.treasury_service;

  /**
   * `GET /DreReport/GetDreReport?startDate&endDate`
   *
   * Retorna a DRE consolidada do intervalo, com `DerivedTotals` + árvore
   * `Sections` recursiva. Usado para KPIs e tabela hierárquica do período.
   */
  public getSinglePeriod(params: {
    startDate: string;
    endDate: string;
  }): Observable<DreReportViewModel> {
    const httpParams = new HttpParams()
      .set('startDate', toBackendDateOnly(params.startDate))
      .set('endDate', toBackendDateOnly(params.endDate));
    return this.http.get<DreReportViewModel>(
      `${this.baseUrl}/DreReport/GetDreReport`,
      { params: httpParams, withCredentials: true },
    );
  }

  /**
   * `GET /DreReport/GetDreReport?startDate&endDate&groupByMonth=true`
   *
   * Retorna a DRE quebrada por mês calendário cheio dentro do intervalo
   * (um item por mês, ordenado cronologicamente). Usado para gráficos
   * mensais e sparklines.
   *
   * O backend SEMPRE quebra por mês calendário cheio, mesmo que o intervalo
   * comece/termine no meio do mês.
   */
  public getMonthlyPeriods(params: {
    startDate: string;
    endDate: string;
  }): Observable<DreReportPeriodViewModel[]> {
    const httpParams = new HttpParams()
      .set('startDate', toBackendDateOnly(params.startDate))
      .set('endDate', toBackendDateOnly(params.endDate))
      .set('groupByMonth', 'true');
    return this.http.get<DreReportPeriodViewModel[]>(
      `${this.baseUrl}/DreReport/GetDreReport`,
      { params: httpParams, withCredentials: true },
    );
  }
}
